import { useState } from 'react';
import type { FormEvent } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { DataTable } from '@/components/DataTable';
import { useCreateRateCard, useRateCards } from '@/hooks/useDesk';
import { formatNadFromCents } from '@/lib/format';

export function CatalogPage() {
  const { data, isLoading, isError, error } = useRateCards();
  const create = useCreateRateCard();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('venue');
  const [price, setPrice] = useState('');

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await create.mutateAsync({
      code,
      name,
      category,
      unit_price_cents: Math.round(Number(price || 0) * 100),
    });
    setCode('');
    setName('');
    setPrice('');
    setOpen(false);
  }

  return (
    <div>
      <PageHeader
        title="Rate list"
        description="Venue, catering, AV and other rate cards."
        actions={
          <button
            type="button"
            className="rounded-lg bg-dome-forest px-3 py-2 text-sm font-medium text-dome-sand"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? 'Close' : 'Add rate'}
          </button>
        }
      />

      {open ? (
        <form
          onSubmit={onSubmit}
          className="mb-5 grid gap-3 rounded-xl border border-[var(--border)] bg-white/80 p-4 sm:grid-cols-2"
        >
          <label className="text-sm">
            <span className="mb-1 block font-medium">Code</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Name</span>
            <input
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Category</span>
            <select
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {['venue', 'catering', 'av', 'stationery', 'other'].map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="mb-1 block font-medium">Unit price (NAD)</span>
            <input
              type="number"
              min={0}
              step={0.01}
              className="w-full rounded-lg border border-[var(--border)] px-3 py-2"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
            />
          </label>
          <div>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-lg bg-dome-brass px-4 py-2 text-sm font-medium text-dome-ink"
            >
              {create.isPending ? 'Saving…' : 'Save rate'}
            </button>
          </div>
        </form>
      ) : null}

      {isLoading ? (
        <p className="animate-pulse-soft text-sm text-[var(--muted)]">Loading…</p>
      ) : null}
      {isError ? (
        <p className="text-sm text-dome-danger">{(error as Error).message}</p>
      ) : null}
      {!isLoading && data && data.length === 0 ? (
        <EmptyState
          title="No rate cards"
          body="Seed domeec_rate_cards here for Quick quote pickers later."
        />
      ) : null}
      {data && data.length > 0 ? (
        <DataTable headers={['Code', 'Name', 'Category', 'Unit', 'Price', 'Active']}>
          {data.map((row) => (
            <tr key={row.id}>
              <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
              <td className="px-3 py-2">{row.name}</td>
              <td className="px-3 py-2 capitalize">{row.category}</td>
              <td className="px-3 py-2">{row.unit}</td>
              <td className="px-3 py-2">
                {formatNadFromCents(row.unit_price_cents)}
              </td>
              <td className="px-3 py-2">{row.is_active ? 'yes' : 'no'}</td>
            </tr>
          ))}
        </DataTable>
      ) : null}
    </div>
  );
}
